'use client';

import React, { createContext, ReactNode, useContext } from 'react';

import { useChat } from '@/providers/ChatContext';
import {
  LastMessage,
  Message,
  MessagesContextProps,
  TemporaryMessage
} from '@/types/chat';
import {
  addMessageToChannel,
  getMessageWithoutDeleted,
  makeReadMessages,
  sortMessages,
  updateMessageById
} from '@/utils/helpers/chat';

const MessagesContext = createContext<MessagesContextProps | undefined>(
  undefined
);

export const MessagesProvider = ({ children }: { children: ReactNode }) => {
  const { setChannels } = useChat();
  const updateLastMessage = (chatId: string, message: LastMessage) => {
    setChannels(prev => handleLastMessage(prev, chatId, message));
  };

  const addMessage = (chatId: string, message: Message | TemporaryMessage) => {
    setChannels(prev =>
      prev.map(channel => {
        if (channel.id !== chatId) {
          return channel;
        }

        return {
          ...channel,
          messages: addMessageToChannel(channel, message)
        };
      })
    );
    updateLastMessage(chatId, message);
  };

  const markMessagesAsRead = (chatId: string, userId: string) => {
    setChannels(prev =>
      prev.map(channel => {
        if (channel.id !== chatId) return channel;

        return {
          ...channel,
          messages: makeReadMessages(channel.messages, userId)
        };
      })
    );
  };

  const removeMessage = (
    chatId: string,
    message: Message | TemporaryMessage
  ) => {
    setChannels(prev =>
      prev.map(channel => {
        if (channel.id !== chatId) {
          return channel;
        }

        return {
          ...channel,
          messages: getMessageWithoutDeleted(message.id, channel.messages)
        };
      })
    );
    updateLastMessage(chatId, message);
  };

  const updateMessageInChannel = (
    chatId: string,
    message: Message | TemporaryMessage,
    updater: (
      oldMessage: Message | TemporaryMessage
    ) => Message | TemporaryMessage
  ) => {
    setChannels(prev =>
      prev.map(channel => {
        if (channel.id !== chatId) return channel;

        return {
          ...channel,
          messages: updateMessageById(channel, message, updater)
        };
      })
    );
  };

  const replaceMessage = (
    chatId: string,
    message: Message | TemporaryMessage,
    newMessage: Message
  ) => {
    updateMessageInChannel(chatId, message, () => newMessage);
  };

  const updateMessageError = (
    chatId: string,
    message: Message | TemporaryMessage,
    isError: boolean
  ) => {
    updateMessageInChannel(chatId, message, oldMessage => ({
      ...oldMessage,
      temporaryError: isError
    }));
  };

  const addMessages = (
    chatId: string,
    messages: (Message | TemporaryMessage)[]
  ) => {
    setChannels(prev =>
      prev.map(channel => {
        if (channel.id !== chatId) return channel;

        return {
          ...channel,
          messages: sortMessages(channel, messages)
        };
      })
    );
  };

  return (
    <MessagesContext.Provider
      value={{
        updateLastMessage,
        addMessage,
        markMessagesAsRead,
        removeMessage,
        updateMessageInChannel,
        replaceMessage,
        updateMessageError,
        addMessages
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = () => {
  const context = useContext(MessagesContext);

  if (!context) {
    throw new Error('useMessages must be used within a MessagesProvider');
  }

  return context;
};
